import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { Loader } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import swal from "sweetalert";
import {
  updateProfile,
  updateProfilePhoto,
} from "../../../../../services/profile";
import {
  changeSignatureNotification,
  getSignatureById,
} from "../../../../../services/signature";
import VerifyPassword from "../../../../../components/VerifyPassword";
import { CreateSignatureModal } from "../../MySignature/CreateSignatureModal";
import { decrypt, showError, showSuccess } from "../../../../../utils";
import { uploadFileToS3Api } from "../../../../../utils/PresignedUrlFunction";
import { useAppTheme } from "../../../../../config/appTheme";
import { THEME_PRESETS } from "../../../../../config/themePresets";
import { hasKnownDesignEntitlement } from "../../../../../utils/entitlements";
import "../settings.css";

/**
 * ProfilePanel — redesign "profile" tab.
 *
 * Reuses the working data-wiring from MyProfie/index.jsx (updateProfile,
 * updateProfilePhoto, VerifyPassword for email change) and MySignature/index.jsx
 * (getSignatureById, changeSignatureNotification, CreateSignatureModal).
 * Only the chrome is re-skinned to the redesign's shared classes.
 *
 * The existing backend stores the logged-in user as a single `name` field, so
 * we split it into first/last for display and recombine on save — matching how
 * MyProfie persists `{ name, npi }`.
 */
export default function ProfilePanel({ profile, role }) {
  const navigate = useNavigate();

  // Prefer redux profile (live updates) but fall back to the prop.
  const reduxProfile = useSelector((state) => state?.profile?.profile);
  const saveProfileLoading = useSelector(
    (state) => state?.profile?.saveProfileLoading
  );
  const { signature, signatureLoading } = useSelector(
    (state) => state?.signature || {}
  );

  const data = reduxProfile || profile;
  const user = data?.user || {};
  const isStaff = (role || user?.type) === "staff";

  // ── Name split (single backend field -> first/last display) ──────────────
  const fullNameInitial = user?.name || "";
  const [firstName, setFirstName] = useState(
    fullNameInitial.split(" ")[0] || ""
  );
  const [lastName, setLastName] = useState(
    fullNameInitial.split(" ").slice(1).join(" ") || ""
  );
  const [email, setEmail] = useState(user?.email || "");
  const [npi, setNpi] = useState(user?.npi || "");
  const [phone, setPhone] = useState(user?.phone || "");

  useEffect(() => {
    const n = user?.name || "";
    setFirstName(n.split(" ")[0] || "");
    setLastName(n.split(" ").slice(1).join(" ") || "");
    setEmail(user?.email || "");
    setNpi(user?.npi || "");
    setPhone(user?.phone || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name, user?.email, user?.npi, user?.phone]);

  // ── Photo upload (reused from MyProfie) ──────────────────────────────────
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoLoading, setSelectedPhotoLoading] = useState(false);
  // True once the user removes their existing saved photo (persisted on Save).
  const [photoRemoved, setPhotoRemoved] = useState(false);
  // The editable "Personal information" form stays hidden (preview-only) until
  // the user hits Edit on the profile hero.
  const [editing, setEditing] = useState(false);
  const openRef = useRef();

  const handlePhotoChange = async (files) => {
    if (!files?.length) return;
    const file = files[0];
    try {
      setSelectedPhotoLoading(true);
      const result = await uploadFileToS3Api({ file });
      setSelectedPhoto(result);
      setPhotoRemoved(false);
    } catch (error) {
      console.error("Upload failed", error);
      showError(error?.message || "Upload failed");
    } finally {
      setSelectedPhotoLoading(false);
    }
  };

  // ── Email-change verify flow (reused from MyProfie) ──────────────────────
  const [opened, { open, close }] = useDisclosure(false);
  const [objForUpdate, setObjForUpdate] = useState(null);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    const name = `${firstName || ""} ${lastName || ""}`.trim();
    if (!name) {
      showError("Name is required");
      return;
    }
    if (!/^\S+@\S+$/.test(email || "")) {
      showError("Invalid email");
      return;
    }
    // Phone is tied to 2FA + integrations — warn before changing it.
    if ((phone || "") !== (user?.phone || "")) {
      const ok = await swal({
        title: "Update phone number?",
        text:
          "This phone number is tied to two-factor authentication and other system integrations. Changing it can disrupt 2FA codes and those integrations until the new number is verified.",
        icon: "warning",
        dangerMode: true,
        buttons: ["Cancel", "Update anyway"],
      });
      if (!ok) return;
    }
    // Persist a photo removal (cleared via Object.assign on the backend).
    const obj = { name, npi, phone, ...(photoRemoved && !selectedPhoto ? { photo: "" } : {}) };
    try {
      if (selectedPhoto) {
        const response = await updateProfilePhoto({ files: selectedPhoto });
        showSuccess(response?.message);
      }
      if (email !== user?.email) {
        open();
        setObjForUpdate({
          name,
          npi,
          phone,
          ...(photoRemoved && !selectedPhoto ? { photo: "" } : {}),
          oldEmail: user?.email,
          newEmail: email,
        });
      } else {
        updateProfile(obj, navigate);
      }
    } catch (error) {
      console.error("Save failed", error);
      showError(error?.message || "Save failed");
    }
  };

  const handleCancel = () => {
    setSelectedPhoto(null);
    setFirstName(fullNameInitial.split(" ")[0] || "");
    setLastName(fullNameInitial.split(" ").slice(1).join(" ") || "");
    setEmail(user?.email || "");
    setNpi(user?.npi || "");
    setPhone(user?.phone || "");
    setEditing(false);
  };

  // ── Appearance (theme preset) ─────────────────────────────────────────────
  // Themes are part of the org's white-label design features and apply to
  // every member (staff included), so they are gated by the ORG's plan.
  const themesEntitled = hasKnownDesignEntitlement(data);
  const [themeKey, setThemeKey] = useAppTheme();

  // ── Signature (reused from MySignature) ──────────────────────────────────
  const [sigOpened, { open: openSig, close: closeSig }] = useDisclosure(false);

  useEffect(() => {
    getSignatureById();
  }, []);

  const handleRequestChange = async () => {
    const response = await changeSignatureNotification({
      signatureId: signature?._id,
      userId: signature?.staff,
    });
    if (response?.status === 200) {
      showSuccess(response?.data?.message);
    } else {
      showError(response?.data?.message);
    }
  };

  // ── Display helpers ──────────────────────────────────────────────────────
  const displayName =
    `${firstName || ""} ${lastName || ""}`.trim() || user?.name || "";
  const initials =
    `${(firstName || "")[0] || ""}${(lastName || "")[0] || ""}`.toUpperCase() ||
    (user?.name || "?")[0]?.toUpperCase() ||
    "?";
  const roleLabel = isStaff
    ? user?.position || "Clinician (Staff)"
    : user?.position || "Administrator";
  // A saved photo only counts while it hasn't been removed this session.
  const currentPhoto = selectedPhoto?.uploadURL || (photoRemoved ? "" : user?.photo);
  const photoSrc = currentPhoto || "/assets/images/avatar.png";
  const canUpdateSignature =
    (isStaff && signature?.signChangeRequest) || !isStaff;

  return (
    <div className="settings-panel active" data-panel="profile">
      <div className="page-header">
        <div>
          <div className="page-title">Profile</div>
          <div className="page-subtitle">
            Your personal account information and signature.
          </div>
        </div>
        {!editing ? (
          <div className="page-actions">
            <button type="button" className="btn" onClick={handleCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn primary"
              onClick={handleSave}
              disabled={saveProfileLoading}
            >
              {saveProfileLoading ? "Saving…" : "Save changes"}
            </button>
          </div>
        ) : null}
      </div>

      <form onSubmit={handleSave}>
        <Dropzone
          onDrop={handlePhotoChange}
          openRef={openRef}
          accept={MIME_TYPES.jpeg | MIME_TYPES.png}
          style={{ display: "none" }}
        />

        {/* ── Identity hero ── */}
        <div className="card">
          <div className="card-section" style={{ position: "relative" }}>
            {!editing ? (
              <button
                type="button"
                className="btn sm profile-edit-toggle"
                onClick={() => setEditing(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit
              </button>
            ) : null}
            <div className="profile-hero">
              <div className="profile-avatar-wrap">
                {selectedPhotoLoading && !selectedPhoto ? (
                  <div className="profile-avatar-loading">
                    <Loader color="#FFBA45" />
                  </div>
                ) : currentPhoto ? (
                  <img className="profile-avatar" src={photoSrc} alt="profile" />
                ) : (
                  <div id="profile-avatar" className="profile-avatar-fallback">
                    {initials}
                  </div>
                )}
                <button
                  type="button"
                  className="profile-avatar-edit"
                  title="Upload new photo"
                  aria-label="Upload new photo"
                  onClick={() => openRef.current?.()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </button>
              </div>

              <div className="profile-id-name">{displayName || "Your name"}</div>
              <div className="profile-id-chips">
                <span className="badge badge-neutral">{roleLabel}</span>
              </div>
              <div className="profile-id-meta">
                {email ? (
                  <span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></svg>
                    {email}
                  </span>
                ) : null}
                {phone ? (
                  <span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                    {phone}
                  </span>
                ) : null}
              </div>

              <div className="profile-photo-tools">
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => openRef.current?.()}
                >
                  {currentPhoto ? "Change photo" : "Upload photo"}
                </button>
                {currentPhoto ? (
                  <button
                    type="button"
                    className="btn sm profile-remove-btn"
                    onClick={() => {
                      setSelectedPhoto(null);
                      // Removing an already-saved photo is persisted on Save.
                      if (user?.photo) setPhotoRemoved(true);
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                    Remove photo
                  </button>
                ) : null}
              </div>

              <div className="profile-hint">
                PNG or JPG, max 4 MB. Shown on notes you sign and in your team's directory.
              </div>
            </div>
          </div>
        </div>

        {/* ── Personal information (revealed only in edit mode) ── */}
        {editing ? (
        <div className="card">
          <div className="card-section">
            <div className="card-title">Personal information</div>
            <div className="card-subtitle">
              Update your name and contact details.
            </div>
            <div className="form-grid two-col" style={{ marginTop: 16 }}>
              <div>
                <label className="label">First name</label>
                <input
                  className="input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="label">Last name</label>
                <input
                  className="input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="label">Phone</label>
                <input
                  className="input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Not set"
                />
                <div className="field-hint">
                  Tied to 2FA &amp; integrations. Changing it may disrupt them.
                </div>
              </div>
            </div>
            <div className="profile-edit-actions">
              <button type="button" className="btn" onClick={handleCancel}>
                Cancel
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={handleSave}
                disabled={saveProfileLoading}
              >
                {saveProfileLoading ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
        ) : null}

        {/* ── Signature (ports MySignature layout) ── */}
        <div className="card">
        <div className="card-section">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 16,
            }}
          >
            <div>
              <div className="card-title">Signature</div>
              <div className="card-subtitle">
                Used to sign notes and documents.
              </div>
            </div>
            {signature && canUpdateSignature ? (
              <button
                type="button"
                className="btn"
                onClick={openSig}
                disabled={signatureLoading}
              >
                Update signature
              </button>
            ) : signature && isStaff ? (
              <button
                type="button"
                className="btn"
                onClick={handleRequestChange}
                disabled={signatureLoading}
              >
                Request change
              </button>
            ) : null}
          </div>

          {signature ? (
            <div className="sig-grid">
              <div className="sig-block">
                <div className="label">Signature</div>
                {signature?.signature ? (
                  <img className="sig-img" src={decrypt(signature?.signature) || ""} alt="signature" />
                ) : signature?.signatureImage ? (
                  <img className="sig-img" src={decrypt(signature?.signatureImage) || ""} alt="signature" />
                ) : (
                  <div className="sig-text">{signature?.signatureText || ""}</div>
                )}
              </div>
              <div className="sig-block">
                <div className="label">Initials</div>
                {signature?.signatureInitials ? (
                  <img className="sig-img sig-img-sm" src={decrypt(signature?.signatureInitials) || ""} alt="initials" />
                ) : signature?.signatureInitialsText ? (
                  <div className="sig-text sig-text-sm">{signature?.signatureInitialsText}</div>
                ) : (
                  <div className="sig-text sig-text-sm" style={{ fontFamily: "inherit", fontSize: 13, color: "var(--muted)" }}>None</div>
                )}
              </div>
            </div>
          ) : (
            <div className="sig-empty">
              <img src="/assets/images/signaturePadIcon.png" alt="" />
              <div className="sig-empty-title">No signature yet</div>
              <div className="sig-empty-sub">
                Add a signature to sign notes and documents.
              </div>
              <button
                type="button"
                className="btn primary"
                onClick={openSig}
                style={{ marginTop: 6 }}
              >
                Create signature
              </button>
            </div>
          )}
        </div>
        </div>

        {/* ── Appearance — theme presets, available to every user ── */}
        <div className="card">
          <div className="card-section">
            <div className="card-title">Appearance</div>
            <div className="card-subtitle" style={{ marginBottom: 16 }}>
              {themesEntitled
                ? "Choose how the app looks for you. Applies instantly."
                : "Themes are part of the white label design features, available on the Gold plan. Your workspace uses the Ogiso standard design."}
            </div>
            <div className="wl-theme-grid" style={themesEntitled ? undefined : { opacity: 0.5, pointerEvents: "none" }}>
              {THEME_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  disabled={!themesEntitled}
                  className={`wl-theme${themeKey === p.key ? " sel" : ""}`}
                  onClick={() => themesEntitled && setThemeKey(p.key)}
                  title={p.label}
                >
                  <span className="wl-theme-swatch" style={{ background: p.tokens.bg }}>
                    <span className="wl-theme-bar" style={{ background: p.tokens["sidebar-bg"] }} />
                    <span className="wl-theme-dot" style={{ background: p.tokens.primary }} />
                  </span>
                  <span className="wl-theme-name">{p.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </form>

      <VerifyPassword
        onclose={close}
        opened={opened}
        data={user}
        objForUpdate={objForUpdate}
      />
      <CreateSignatureModal opened={sigOpened} onClose={closeSig} />
    </div>
  );
}
